import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
// NGO signup deactivated 2026-07-09 — restore submitNgoSignup import if re-enabling
import { submitIndividualSignup /*, submitNgoSignup */ } from '../lib/supabase'

const MODAL_STEPS = { NONE: 'none', CHOOSE: 'choose', INDIVIDUAL: 'individual', NGO: 'ngo', SUCCESS: 'success' }

const InputField = ({ label, value, onChange, placeholder, type = 'text', required = true }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1 font-poppins">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full px-3 py-2 rounded-md border border-gray-300 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
    />
  </div>
)

const ModalWrapper = ({ children, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        {children}
      </motion.div>
    </motion.div>
  </AnimatePresence>
)

// NGO signup deactivated 2026-07-09 — the chooser modal is bypassed; "I'm Interested" opens the individual form directly. Uncomment to restore.
/*
const ChooseModal = ({ onChoose, onClose }) => (
  <ModalWrapper onClose={onClose}>
    <h3 className="text-lg font-bold text-gray-800 font-poppins mb-1 text-center">I'm Interested!</h3>
    <p className="text-xs text-gray-500 text-center mb-5 font-lato">How would you like to join?</p>
    <div className="flex flex-col gap-3">
      <button
        onClick={() => onChoose('individual')}
        className="w-full py-3 px-4 rounded-lg font-semibold text-white text-sm transition-all duration-300 hover:scale-105"
        style={{ backgroundColor: '#A96D1A' }}
      >
        👤 As an Individual
      </button>
      <button
        onClick={() => onChoose('ngo')}
        className="w-full py-3 px-4 rounded-lg font-semibold text-white text-sm transition-all duration-300 hover:scale-105"
        style={{ backgroundColor: '#29425D' }}
      >
        🏢 My NGO might be Interested
      </button>
    </div>
  </ModalWrapper>
)
*/

const IndividualModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ name: '', country: '', email: '', whatsapp: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await submitIndividualSignup({
        full_name: form.name.trim(),
        country: form.country.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
      })
      onSuccess()
    } catch (err) {
      console.error(err)
      setError("Something went wrong. Please try again, or email us directly.")
      setSubmitting(false)
    }
  }

  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-bold text-gray-800 font-poppins mb-1">Join as Individual</h3>
      <p className="text-xs text-gray-500 mb-3 font-lato">Raise your hand — we'll be in touch!</p>
      <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2.5">
        <p className="text-xs font-semibold text-red-700 font-poppins mb-0.5">Heads up — this batch is now full.</p>
        <p className="text-xs text-red-700/90 font-lato leading-snug">Please go ahead and share your details below. We'll save you a seat in the next batch and reach out as soon as it opens.</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <InputField label="Full Name" value={form.name} onChange={set('name')} placeholder="Your full name" />
        <InputField label="Country" value={form.country} onChange={set('country')} placeholder="Your country" />
        <InputField label="Email Address" value={form.email} onChange={set('email')} placeholder="your@email.com" type="email" />
        <InputField label="WhatsApp Number" value={form.whatsapp} onChange={set('whatsapp')} placeholder="+1 234 567 8900" type="tel" />
        {error && <p className="text-xs text-red-600 font-lato">{error}</p>}
        <button type="submit" disabled={submitting} className="w-full py-2.5 rounded-lg font-semibold text-white text-sm mt-1 transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100" style={{ backgroundColor: '#A96D1A' }}>
          {submitting ? 'Submitting…' : 'Submit My Interest'}
        </button>
      </form>
    </ModalWrapper>
  )
}

// NGO signup deactivated 2026-07-09 — uncomment to restore.
/*
const NGOModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ ngoName: '', city: '', country: '', fullName: '', email: '', whatsapp: '', edName: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await submitNgoSignup({
        ngo_name: form.ngoName.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        contact_name: form.fullName.trim(),
        contact_email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        ngo_director: form.edName.trim(),
      })
      onSuccess()
    } catch (err) {
      console.error(err)
      setError("Something went wrong. Please try again, or email us directly.")
      setSubmitting(false)
    }
  }

  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-bold text-gray-800 font-poppins mb-1">NGO Interest</h3>
      <p className="text-xs text-gray-500 mb-4 font-lato">Tell us about your organisation.</p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <InputField label="NGO Name" value={form.ngoName} onChange={set('ngoName')} placeholder="Organisation name" />
        <div className="grid grid-cols-2 gap-2">
          <InputField label="City / Location" value={form.city} onChange={set('city')} placeholder="City" />
          <InputField label="Country" value={form.country} onChange={set('country')} placeholder="Country" />
        </div>
        <InputField label="Your Full Name" value={form.fullName} onChange={set('fullName')} placeholder="Your name" />
        <InputField label="Your Email Address" value={form.email} onChange={set('email')} placeholder="your@email.com" type="email" />
        <InputField label="WhatsApp Number" value={form.whatsapp} onChange={set('whatsapp')} placeholder="+1 234 567 8900" type="tel" />
        <InputField label="Name of NGO Director" value={form.edName} onChange={set('edName')} placeholder="Director's full name..." />
        {error && <p className="text-xs text-red-600 font-lato">{error}</p>}
        <button type="submit" disabled={submitting} className="w-full py-2.5 rounded-lg font-semibold text-white text-sm mt-1 transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100" style={{ backgroundColor: '#29425D' }}>
          {submitting ? 'Submitting…' : 'Submit Our Interest'}
        </button>
      </form>
    </ModalWrapper>
  )
}
*/

const SuccessModal = ({ onClose }) => (
  <ModalWrapper onClose={onClose}>
    <div className="text-center py-4">
      <div className="text-5xl mb-3">🎉</div>
      <h3 className="text-lg font-bold text-gray-800 font-poppins mb-2">Thank You!</h3>
      <p className="text-sm text-gray-600 font-lato mb-4">Your interest has been registered. We'll be in touch soon with more details about joining the WhatsApp Street Outreach Course.</p>
      <button onClick={onClose} className="px-6 py-2.5 rounded-lg font-semibold text-white text-sm transition-all duration-300 hover:scale-105" style={{ backgroundColor: '#A96D1A' }}>
        Close
      </button>
    </div>
  </ModalWrapper>
)

const Hero = () => {
  const [modalStep, setModalStep] = useState(MODAL_STEPS.NONE)
  const closeModal = () => setModalStep(MODAL_STEPS.NONE)

  return (
    <section className="relative h-full flex items-center justify-center overflow-hidden pt-20" style={{ backgroundColor: '#A96D1A' }}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full animate-pulse" style={{ backgroundColor: 'rgba(255,177,144,0.1)' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full animate-pulse" style={{ backgroundColor: 'rgba(255,177,144,0.05)' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="lg:col-span-2 text-left"
          >
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8, ease: 'easeOut' }}
              className="text-4xl lg:text-5xl font-bold text-white mb-4 font-poppins leading-tight"
            >
              From the Streets Up:{' '}
              <span style={{ color: '#FFB190' }}>20 Lessons for Better Outreach</span>
            </motion.h1>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
              className="text-2xl lg:text-3xl font-semibold text-white/90 mb-4 font-poppins leading-tight"
            >
              1 Key Topic a Week. <span className="italic">Field-Ready. No Fluff.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
              className="text-white/90 mb-5 leading-relaxed font-lato max-w-lg"
              style={{ fontSize: '1.2rem' }}
            >
              A FREE WhatsApp Course for Frontline Workers. 20 Weekly lessons with practical tools, fun quizzes, and audio shows — straight to your phone.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.8, ease: 'easeOut' }}
              className="flex flex-wrap gap-3 mb-5"
            >
              {['📱 WhatsApp Community', '🎯 20 Frontline Topics', '🎧 Audio Shows', '✅ Fun Quizzes'].map((tag) => (
                <span key={tag} className="text-xs font-semibold px-3 py-1.5 rounded-full text-white/90 font-poppins" style={{ backgroundColor: 'rgba(255,177,144,0.25)', border: '1px solid rgba(255,177,144,0.4)' }}>
                  {tag}
                </span>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
              className="mb-6"
            >
              <p className="text-white/90 font-semibold text-lg font-poppins">
                Start Date: <span style={{ color: '#FFB190' }}>Monday 11 May 2026</span>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
            >
              {/* NGO signup deactivated 2026-07-09 — was setModalStep(MODAL_STEPS.CHOOSE) to open the individual/NGO chooser */}
              <button
                onClick={() => setModalStep(MODAL_STEPS.INDIVIDUAL)}
                className="px-8 py-3.5 text-base font-bold rounded-md transition-all duration-300 hover:scale-105 hover:shadow-xl font-poppins"
                style={{ backgroundColor: '#82A6C3', color: 'white' }}
              >
                🙋 I'm Interested!
              </button>
              <p className="text-white/70 text-sm font-lato mt-2">Free to join.</p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
            className="flex justify-center lg:justify-end"
          >
            <img
              src="https://images.groovetech.io/AIeg2_WjoOO4YMCw_2ySgISCh9XTlYKmHluTMnOgGBI/rs:fit:0:0:0/g:no:0:0/c:0:0/aHR0cHM6Ly9hc3NldHMuZ3Jvb3ZlYXBwcy5jb20vaW1hZ2VzL2MxZTdhMGZlLTQ0NGItNDEzNy04NDNiLTg5ZmRhZmUwYjVkMS8xNzI4OTgxNzczX0hFUk9MZXNzU0NBUlMuSlBH.webp"
              alt="Child Portrait"
              className="w-full max-w-xs rounded-lg shadow-xl"
              style={{ maxWidth: '345px' }}
            />
          </motion.div>
        </div>
      </div>

      {/* NGO signup deactivated 2026-07-09 — uncomment these two lines (and the ChooseModal/NGOModal components above) to restore:
      {modalStep === MODAL_STEPS.CHOOSE && <ChooseModal onChoose={(type) => setModalStep(type === 'individual' ? MODAL_STEPS.INDIVIDUAL : MODAL_STEPS.NGO)} onClose={closeModal} />}
      {modalStep === MODAL_STEPS.NGO && <NGOModal onClose={closeModal} onSuccess={() => setModalStep(MODAL_STEPS.SUCCESS)} />}
      */}
      {modalStep === MODAL_STEPS.INDIVIDUAL && <IndividualModal onClose={closeModal} onSuccess={() => setModalStep(MODAL_STEPS.SUCCESS)} />}
      {modalStep === MODAL_STEPS.SUCCESS && <SuccessModal onClose={closeModal} />}
    </section>
  )
}

export default Hero